import { HttpClient, httpResource } from '@angular/common/http';
import { Injectable, computed, inject } from '@angular/core';

import { Observable, map } from 'rxjs';
import * as z from 'zod';

import { Client } from '../schemas';
import { storedResource } from '../shared';
import { Lambda } from './lambda';
import { Messenger } from './messenger';

@Injectable({ providedIn: 'root' })
export class ClientService {
  private http = inject(HttpClient);

  private readonly url = inject(Lambda).getClientsUrl();

  private readonly CACHE_KEY = `lcm:service-cache:client`;

  private clientsResource = httpResource<Client[]>(() => this.url, {
    defaultValue: JSON.parse(window.localStorage.getItem(this.CACHE_KEY) || '[]'),
    parse: z.array(Client).parse,
  });

  constructor() {
    storedResource(this.clientsResource, this.CACHE_KEY, inject(Messenger));
  }

  clients = this.clientsResource.value;
  pending = this.clientsResource.isLoading;

  names = computed<Record<string, string>>(() =>
    this.clients().reduce((mapping, client) => {
      mapping[client.ClientCode] = client.ClientName;
      return mapping;
    }, {}),
  );

  codes = computed(() =>
    this.clients()
      .map((item) => item.ClientCode)
      .sort(),
  );

  reload() {
    this.clientsResource.reload();
  }

  save(instance: Client, forEdit = false): Observable<Client> {
    let request: Observable<unknown>;

    if (forEdit) {
      const url = this.url + '/' + instance.ClientCode;
      request = this.http.put(url, instance);
    } else {
      request = this.http.post(this.url, instance);
    }

    return request.pipe(map((data) => Client.parse(data)));
  }
}
